import { type SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPermissionRepository } from '@webapp/platform/iam/business-logic/interfaces/IPermissionRepository'
import { type ClearSecondaryCategoryReportAccessData } from '@webapp/platform/iam/business-logic/types/ClearSecondaryCategoryReportAccessData'
import { type GrantReportAccessData } from '@webapp/platform/iam/business-logic/types/GrantReportAccessData'
import { type GrantReportAccessToAllSecondaryCategoriesData } from '@webapp/platform/iam/business-logic/types/GrantReportAccessToAllSecondaryCategoriesData'
import { type ListReportAccessGrantsData } from '@webapp/platform/iam/business-logic/types/ListReportAccessGrantsData'
import { type PermissionFilterGrant } from '@webapp/platform/iam/business-logic/types/PermissionFilterGrant'
import { type ReportAccessForAllPrimaryCategoriesData } from '@webapp/platform/iam/business-logic/types/ReportAccessForAllPrimaryCategoriesData'
import { ObjectPermissions } from '@webapp/platform/iam/entities/enums/ObjectPermissionEnum'
import { type PermissionConstructor } from '@webapp/platform/iam/entities/Permission'
import { type ReportAccessGrantConstructor } from '@webapp/platform/iam/entities/ReportAccessGrant'
import { type PermissionApiModel } from '../../api-models/PermissionApiModel'
import { mapPermissionApiModel } from '../../mapper/mapPermissionApiModel'
import { mapReportAccessGrantApiModel } from '../../mapper/mapReportAccessGrantApiModel'
import {
  type AssignPermissionOptions,
  type AssignUnassignFilterGroup,
  type PermissionFilterPayload,
} from '../../types/assignPermissionPayload'
import { type DeleteReportAccessFromAllPrimaryCategoriesPayload } from '../../types/DeleteReportAccessFromAllPrimaryCategoriesPayload'
import { type DeleteSecondaryCategoryReportAccessPayload } from '../../types/DeleteSecondaryCategoryReportAccessPayload'
import { type GetReportAccessGrantsResponse } from '../../types/GetReportAccessGrantsResponse'
import { type PostReportAccessGrantPayload } from '../../types/PostReportAccessGrantPayload'
import { type PostReportAccessGrantResponse } from '../../types/PostReportAccessGrantResponse'
import { type PostReportAccessToAllPrimaryCategoriesPayload } from '../../types/PostReportAccessToAllPrimaryCategoriesPayload'
import { type PostReportAccessToAllSecondaryCategoriesPayload } from '../../types/PostReportAccessToAllSecondaryCategoriesPayload'

interface Dependency {
  httpClient: IHttpClient
}

export class PermissionRepository implements IPermissionRepository {
  constructor(private readonly deps: Dependency) {}

  async getSystemPermissions(): Promise<
    RepoStandardResponse<PermissionConstructor[]>
  > {
    const res = await this.deps.httpClient.get<
      ApiResponse<PermissionApiModel[]>
    >(`/api/v3/permissions/`, {
      params: {
        page_size: 'all',
      },
    })

    const permissions = res.data.data

    return {
      data: permissions.map((permission) => mapPermissionApiModel(permission)),
    }
  }

  async assignFilters(grants: PermissionFilterGrant[]): Promise<void> {
    try {
      await this.deps.httpClient.put<
        PermissionFilterPayload[],
        PermissionFilterPayload[]
      >(
        `/api/v3/permissions/filters/assign/`,
        grants.map((grant) => ({
          object_id: grant.objectId,
          content_type: grant.contentTypeId,
          user: grant.userAuthId,
          permission: grant.permissionId,
        }))
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async assignToPrimaryCategories(
    permissionId: number,
    userAuthId: number,
    primaryCategoryContentTypeId: number,
    primaryCategoryIds: number[],
    { all = false }: AssignPermissionOptions = {}
  ): Promise<void> {
    try {
      await this.deps.httpClient.put<
        RepoStandardResponse<number[]>,
        AssignUnassignFilterGroup[]
      >(`/api/v3/permissions/filters/assign-unassign/`, [
        {
          all,
          filters: primaryCategoryIds.map((primaryCategoryId) => ({
            object_id: primaryCategoryId,
            content_type: primaryCategoryContentTypeId,
            user: userAuthId,
            permission: permissionId,
          })),
          permission: permissionId,
          user: userAuthId,
          content_type: all ? primaryCategoryContentTypeId : undefined,
          editing_scope: 'location',
        },
      ])
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async assignToSecondaryCategories(
    permissionId: number,
    userAuthId: number,
    primaryCategoryContentTypeId: number,
    primaryCategoryIds: number[],
    secondaryCategoryContentTypeId: number,
    secondaryCategories: SecondaryCategory[],
    { all = false }: AssignPermissionOptions = {}
  ): Promise<void> {
    try {
      const secondaryCategoryIds = secondaryCategories.map((s) => s.id)

      await this.deps.httpClient.put<
        RepoStandardResponse<number[]>,
        AssignUnassignFilterGroup[]
      >(`/api/v3/permissions/filters/assign-unassign/`, [
        {
          all,
          filters: [
            ...secondaryCategoryIds.map((secondaryCategoryId) => ({
              object_id: secondaryCategoryId,
              content_type: secondaryCategoryContentTypeId,
              user: userAuthId,
              permission: permissionId,
            })),
            ...primaryCategoryIds.map((primaryCategoryId) => ({
              object_id: primaryCategoryId,
              content_type: primaryCategoryContentTypeId,
              user: userAuthId,
              permission: permissionId,
            })),
          ],
          permission: permissionId,
          user: userAuthId,
          content_type: all ? secondaryCategoryContentTypeId : undefined,
          editing_scope: 'department',
        },
      ])
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listReportAccessGrants(
    params: ListReportAccessGrantsData
  ): Promise<ReportAccessGrantConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<GetReportAccessGrantsResponse>(
        `/api/v3/permissions/objects/`,
        {
          params: {
            content_type: params.secondaryCategoryContentTypeId,
            object_pk: params.secondaryCategoryId,
            order_by: 'user.lastName',
            permission: ObjectPermissions.VIEW_REPORT,
          },
        }
      )

      // The endpoint ignores `order_by`, so the last-name order is applied here
      return res.data.data
        .map((grant) => mapReportAccessGrantApiModel(grant))
        .sort((a, b) =>
          a.userLastName.localeCompare(b.userLastName, undefined, {
            sensitivity: 'base',
          })
        )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccess(
    payload: GrantReportAccessData
  ): Promise<ReportAccessGrantConstructor> {
    try {
      const res = await this.deps.httpClient.post<
        PostReportAccessGrantResponse,
        PostReportAccessGrantPayload
      >(`/api/v3/permissions/objects/`, {
        user: payload.userId,
        content_type: payload.secondaryCategoryContentTypeId,
        permission: ObjectPermissions.VIEW_REPORT,
        object: payload.secondaryCategoryId,
      })

      return mapReportAccessGrantApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async revokeReportAccessGrant(grantId: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        `/api/v3/permissions/objects/${grantId}/`
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async clearSecondaryCategoryReportAccess(
    payload: ClearSecondaryCategoryReportAccessData
  ): Promise<void> {
    try {
      await this.deps.httpClient.delete<
        void,
        DeleteSecondaryCategoryReportAccessPayload
      >(`/api/v3/permissions/objects/object/`, {
        data: {
          content_type: payload.secondaryCategoryContentTypeId,
          permission: ObjectPermissions.VIEW_REPORT,
          object: payload.secondaryCategoryId,
        },
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccessToAllSecondaryCategories(
    payload: GrantReportAccessToAllSecondaryCategoriesData
  ): Promise<void> {
    try {
      await this.deps.httpClient.post<
        void,
        PostReportAccessToAllSecondaryCategoriesPayload
      >(`/api/v3/reports/permissions/`, {
        location: payload.primaryCategoryId,
        user: payload.userId,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async grantReportAccessToAllPrimaryCategories(
    payload: ReportAccessForAllPrimaryCategoriesData
  ): Promise<void> {
    try {
      await this.deps.httpClient.post<
        void,
        PostReportAccessToAllPrimaryCategoriesPayload
      >(`/api/v3/permissions/objects/user/`, {
        user: payload.userId,
        content_type: payload.secondaryCategoryContentTypeId,
        permission: ObjectPermissions.VIEW_REPORT,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async revokeReportAccessFromAllPrimaryCategories(
    payload: ReportAccessForAllPrimaryCategoriesData
  ): Promise<void> {
    try {
      await this.deps.httpClient.delete<
        void,
        DeleteReportAccessFromAllPrimaryCategoriesPayload
      >(`/api/v3/permissions/objects/user/`, {
        data: {
          user: payload.userId,
          content_type: payload.secondaryCategoryContentTypeId,
          permission: ObjectPermissions.VIEW_REPORT,
        },
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
